/**
 * Presence Service - Track what users are doing in real-time
 *
 * This service allows users to see what the other user is currently doing:
 * - "User 2 is planning..."
 * - "User 2 is typing..."
 * - "User 2 is selecting location..."
 * etc.
 */

import { doc, setDoc, onSnapshot, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export type UserAction =
  | 'idle'
  | 'planning'
  | 'typing'
  | 'proposing_call'
  | 'proposing_video'
  | 'in_call'
  | 'in_video'
  | 'selecting_location'
  | 'on_the_way'
  | 'in_meeting';

export interface UserPresence {
  userId: string;
  userName: string;
  currentAction: UserAction;
  lastActionAt: Timestamp;
  metadata?: {
    [key: string]: any;
  };
}

/**
 * Update the current user's presence
 */
export async function updatePresence(
  connectionId: string,
  userId: string,
  userName: string,
  action: UserAction,
  metadata?: any
): Promise<void> {
  try {
    const presenceRef = doc(db, 'connectionFlows', connectionId, 'presence', userId);

    await setDoc(presenceRef, {
      userId,
      userName,
      currentAction: action,
      lastActionAt: serverTimestamp(),
      metadata: metadata || {}
    }, { merge: true });

    console.log(`📡 [Presence] Updated: ${userName} is ${action}`);
  } catch (error) {
    console.error('❌ [Presence] Error updating presence:', error);
  }
}

/**
 * Listen to other user's presence
 */
export function listenToPresence(
  connectionId: string,
  otherUserId: string,
  onPresenceChange: (presence: UserPresence | null) => void
): () => void {
  const presenceRef = doc(db, 'connectionFlows', connectionId, 'presence', otherUserId);

  const unsubscribe = onSnapshot(presenceRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data() as UserPresence;
      onPresenceChange(data);
    } else {
      onPresenceChange(null);
    }
  }, (error) => {
    console.error('❌ [Presence] Error listening to presence:', error);
    onPresenceChange(null);
  });

  return unsubscribe;
}

/**
 * Get human-readable action label
 */
export function getActionLabel(action: UserAction): string {
  const labels: Record<UserAction, string> = {
    idle: '',
    planning: 'is aan het plannen',
    typing: 'is aan het typen',
    proposing_call: 'wil bellen',
    proposing_video: 'wil videobellen',
    in_call: 'is in gesprek',
    in_video: 'is aan het videobellen',
    selecting_location: 'selecteert een locatie',
    on_the_way: 'is onderweg',
    in_meeting: 'is in meeting'
  };

  return labels[action] || '';
}

/**
 * Get icon for action
 */
export function getActionIcon(action: UserAction): string {
  const icons: Record<UserAction, string> = {
    idle: '',
    planning: '📅',
    typing: '✍️',
    proposing_call: '📞',
    proposing_video: '📹',
    in_call: '📞',
    in_video: '📹',
    selecting_location: '📍',
    on_the_way: '🚗',
    in_meeting: '🤝'
  };

  return icons[action] || '';
}
